/**
 * @file Agent 工作台 components\AgentTracePanel.vue
 */
import {
  AgentGoalType,
  AgentStepStatus,
  AgentTaskStatus,
  ToolExecutionStatus,
  ToolPermissionAction,
  ToolRiskLevel,
} from "~/enums";
import type { ThinkingStep } from "~/composables/useThinkingProcess";
import type { AgentTaskStep, AgentTraceLog } from "~/types/agent-workbench";

const AGENT_TASK_STATUS_TEXT: Record<AgentTaskStatus, string> = {
  [AgentTaskStatus.CREATED]: "已创建",
  [AgentTaskStatus.PLANNING]: "规划中",
  [AgentTaskStatus.EXECUTING]: "执行中",
  [AgentTaskStatus.COMPLETED]: "已完成",
  [AgentTaskStatus.FAILED]: "失败",
  [AgentTaskStatus.CANCELLED]: "已取消",
};

const AGENT_STEP_STATUS_TEXT: Record<AgentStepStatus, string> = {
  [AgentStepStatus.PENDING]: "等待",
  [AgentStepStatus.RUNNING]: "执行中",
  [AgentStepStatus.SUCCESS]: "成功",
  [AgentStepStatus.FAILED]: "失败",
  [AgentStepStatus.SKIPPED]: "跳过",
};

const THINKING_STEP_STATUS_TEXT: Record<
  NonNullable<ThinkingStep["status"]>,
  string
> = {
  pending: "进行中",
  completed: "已完成",
  error: "失败",
};

const TOOL_STATUS_TEXT: Record<ToolExecutionStatus, string> = {
  [ToolExecutionStatus.PENDING]: "等待",
  [ToolExecutionStatus.RUNNING]: "执行中",
  [ToolExecutionStatus.SUCCESS]: "成功",
  [ToolExecutionStatus.FAILED]: "失败",
  [ToolExecutionStatus.SKIPPED]: "跳过",
};

const TOOL_RISK_TEXT: Record<ToolRiskLevel, string> = {
  [ToolRiskLevel.LOW]: "低风险",
  [ToolRiskLevel.MEDIUM]: "中风险",
  [ToolRiskLevel.HIGH]: "高风险",
};

const TOOL_PERMISSION_TEXT: Record<ToolPermissionAction, string> = {
  [ToolPermissionAction.READ]: "读取",
  [ToolPermissionAction.WRITE]: "写入",
  [ToolPermissionAction.DELETE]: "删除",
  [ToolPermissionAction.NETWORK]: "网络",
  [ToolPermissionAction.ADMIN]: "管理",
};

const AGENT_GOAL_TYPE_TEXT: Record<AgentGoalType, string> = {
  [AgentGoalType.QUESTION]: "问答任务",
  [AgentGoalType.CODING]: "代码任务",
  [AgentGoalType.RESEARCH]: "调研任务",
  [AgentGoalType.FILE_ANALYSIS]: "文件分析",
  [AgentGoalType.LONG_RUNNING_TASK]: "长任务",
};

const FILE_PATH_KEYS = ["file", "path", "filePath", "target", "filename"];
const SUMMARY_KEYS = [
  "summary",
  "message",
  "content",
  "text",
  "path",
  "file",
  "result",
];

/**
 * 获取任务状态文案
 * @param status
 * @returns
 */
export const getTaskStatusText = (status: AgentTaskStatus) =>
  AGENT_TASK_STATUS_TEXT[status] || status;

/**
 * 获取步骤状态文案
 * @param status
 * @returns
 */
export const getStepStatusText = (status: AgentStepStatus) =>
  AGENT_STEP_STATUS_TEXT[status] || status;

/**
 * 获取思考步骤状态文案
 * @param status
 * @returns
 */
export const getThinkingStatusText = (status?: ThinkingStep["status"]) =>
  status ? THINKING_STEP_STATUS_TEXT[status] : "已完成";

/**
 * 获取思考步骤状态样式
 * @param status
 * @returns
 */
export const normalizeThinkingStatusClass = (
  status?: ThinkingStep["status"],
) => {
  if (status === "pending") {
    return AgentStepStatus.RUNNING;
  }

  if (status === "error") {
    return AgentStepStatus.FAILED;
  }

  return AgentStepStatus.SUCCESS;
};

/**
 * 获取工具执行状态文案
 * @param status
 * @returns
 */
export const getToolStatusText = (status: ToolExecutionStatus) =>
  TOOL_STATUS_TEXT[status] || status;

/**
 * 获取工具风险文案
 * @param riskLevel
 * @returns
 */
export const getRiskText = (riskLevel: ToolRiskLevel) =>
  TOOL_RISK_TEXT[riskLevel] || riskLevel;

/**
 * 获取工具权限文案
 * @param permissionAction
 * @returns
 */
export const getPermissionText = (permissionAction: ToolPermissionAction) =>
  TOOL_PERMISSION_TEXT[permissionAction] || permissionAction;

/**
 * 获取任务类型文案
 */
export const getGoalTypeText = (goalType: AgentGoalType) =>
  AGENT_GOAL_TYPE_TEXT[goalType] || "Agent 任务";

/**
 * 获取步骤耗时文案
 * @param step
 * @returns
 */
export const getStepDurationText = (step: AgentTaskStep) => {
  const startedAt = toTimestamp(step.startedAt || step.createdAt);
  const finishedAt = toTimestamp(step.finishedAt || step.updatedAt);
  if (startedAt && finishedAt && finishedAt >= startedAt) {
    return formatDuration(finishedAt - startedAt);
  }

  return formatDate(step.updatedAt || step.createdAt);
};

/**
 * 判断是否为文件变更日志
 * @param log
 * @returns
 */
export const isFileChangeLog = (log: AgentTraceLog) =>
  [
    ToolPermissionAction.WRITE,
    ToolPermissionAction.DELETE,
    ToolPermissionAction.ADMIN,
  ].includes(log.permissionAction as ToolPermissionAction);

/**
 * 获取文件变更日志的文件路径
 * @param log
 * @returns
 */
export const getFilePathFromLog = (log: AgentTraceLog) => {
  const input = isRecord(log.input) ? log.input : {};
  const matchedKey = FILE_PATH_KEYS.find(
    (key) => typeof input[key] === "string",
  );
  if (matchedKey) {
    return String(input[matchedKey]);
  }

  return getPreviewSummary(log.input) || "未提供文件路径";
};

/**
 * 获取文件变更日志的摘要
 * @param value
 * @returns
 */
export const getPreviewSummary = (value: unknown): string => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return compactText(formatJsonString(value));
  }

  const data = isRecord(value) && "data" in value ? value.data : value;
  if (typeof data === "string") {
    return compactText(formatJsonString(data));
  }

  if (isRecord(data)) {
    const matchedKey = SUMMARY_KEYS.find(
      (key) => typeof data[key] === "string",
    );
    if (matchedKey) {
      return compactText(String(data[matchedKey]));
    }

    return compactText(Object.keys(data).slice(0, 6).join("、"));
  }

  try {
    return compactText(JSON.stringify(data));
  } catch {
    return compactText(String(data));
  }
};

/**
 * 格式化 JSON 字符串
 * @param value
 * @returns
 */
export const formatJsonString = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value));
  } catch {
    return value;
  }
};

/**
 * 压缩文本
 * @param value
 * @returns
 */
export const compactText = (value: string) => {
  const text = value.replace(/\s+/g, " ").trim();
  return text.length > 160 ? `${text.slice(0, 160)}...` : text;
};

/**
 * 获取时间文本
 * @param value
 * @returns
 */
export const formatDate = (value?: number | string | Date) => {
  const timestamp = toTimestamp(value);
  if (!timestamp) {
    return "";
  }

  return new Date(timestamp).toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * 获取时长文本
 * @param durationMs
 * @returns
 */
export const formatDuration = (durationMs?: number) => {
  if (!durationMs || durationMs < 0) {
    return "0ms";
  }

  if (durationMs < 1000) {
    return `${durationMs}ms`;
  }

  return `${(durationMs / 1000).toFixed(1)}s`;
};

/**
 * 时间戳转换
 * @param value
 * @returns
 */
export const toTimestamp = (value?: number | string | Date) => {
  if (!value) {
    return 0;
  }

  const date = value instanceof Date ? value : new Date(value);
  const timestamp = date.getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

/**
 * 判断是否为 Record
 * @param value
 * @returns
 */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * 获取可选字符串
 * @param value
 * @returns
 */
export const normalizeOptionalString = (value?: string) => {
  const normalized = String(value || "").trim();
  return normalized || undefined;
};

/**
 * 构建查询参数
 * @param query
 * @returns
 */
export const buildQuery = (
  query: Record<string, string | number | undefined>,
) =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== undefined && value !== "",
    ),
  );

/**
 * 获取工作台加载错误信息
 * @param error
 * @param fallback
 * @returns
 */
export const getWorkbenchLoadError = (error: unknown, fallback: string) => {
  const errorRecord = isRecord(error) ? error : {};
  const message = String(
    errorRecord.message || errorRecord.statusMessage || "",
  ).trim();
  if (
    !message ||
    message.includes("Failed to fetch") ||
    message.includes("<no response>")
  ) {
    return fallback;
  }

  return message;
};
